import { EuiButton, EuiSearchBar } from '@elastic/eui'
import { useAppDispatch, useAppSelector, useClientBuildings } from 'modules/module-utils'
import { Grid, useToggle, useTranslate } from 'modules/web-atoms'
import { DeleteConfirmationModal, Table } from 'modules/web-molecules'
import { IRadioSelectorItem } from 'modules/web-organisms'
import { ICreateOptions, IDeleteOptions, ISearchOptions } from 'modules/web-templates'
import React from 'react'
import { useSizesTable } from '../../../hooks/use-sizes-table/use-sizes-table'
import settings, { SettingsFormState } from '../../../settings'

const TABLE_ID = 'table-sizes'
const INTL_PREFIX_MODAL = 'settings.tabs.sizes.delete-modal'

const SizesTable: React.FC = () => {
  const t = useTranslate('settings.tabs.sizes.table')
  const dispatch = useAppDispatch()

  const [isDeleteModalOpen, toggleDeleteModal] = useToggle(false)

  const selectedItems = useAppSelector(settings.selectors.selectedSizes)
  const selectedItem = useAppSelector(settings.selectors.selectedSize)
  const sizeFormState = useAppSelector(settings.selectors.sizeFormState)
  const selectedSite = useAppSelector(settings.selectors.selectedSite)
  const isAdding = useAppSelector(settings.selectors.isAddingSize)
  const isDeleting = useAppSelector(settings.selectors.isDeletingSizes)
  const { canManageSizes } = useAppSelector(settings.selectors.permissions)

  const { buildings, isFetching: isFetchingBuildings } = useClientBuildings()
  const siteOptions: IRadioSelectorItem[] = React.useMemo(
    () =>
      buildings?.map((building) => ({
        id: building.id.toString(),
        label: building.buildingName,
      })) ?? [],
    [buildings],
  )

  const {
    columns,
    items,
    selection,
    onItemClicked,
    isFetching,
    sort,
    onSearch,
    error,
    pagination,
    paginationOptions,
    selectedSizes,
  } = useSizesTable(selectedSite)

  const isLoading = isFetching || isFetchingBuildings

  React.useEffect(() => {
    if (siteOptions && siteOptions.length) {
      dispatch(settings.actions.setSelectedSite(parseInt(siteOptions[0].id)))
    }
  }, [siteOptions, dispatch])

  const onAddClick = React.useCallback(() => {
    dispatch(settings.actions.setSizeFormState(SettingsFormState.CREATE))
  }, [dispatch])

  const createOptions: ICreateOptions = React.useMemo(
    () => ({
      visible: canManageSizes && sizeFormState !== SettingsFormState.CREATE,
      onClick: onAddClick,
      value: t('add'),
    }),
    [sizeFormState, canManageSizes, onAddClick, t],
  )

  const onDelete = React.useCallback(() => {
    dispatch(settings.actions.deleteSizes())
    toggleDeleteModal()
  }, [dispatch, toggleDeleteModal])

  const searchOptions: ISearchOptions = React.useMemo(
    () => ({
      hint: t('search.placeholder'),
      onSearch,
    }),
    [t, onSearch],
  )

  const deleteOptions: IDeleteOptions = React.useMemo(
    () => ({
      visible: (selectedItems.length > 0 || !!selectedItem) && canManageSizes,
      isLoading: isDeleting,
      onClick: onDelete,
      value: t('delete', { count: selectedItems.length || 1 }),
    }),
    [selectedItems.length, selectedItem, isDeleting, onDelete, canManageSizes, t],
  )

  const onQuery = React.useCallback(
    ({ queryText: filter }) => {
      searchOptions?.onSearch(filter)
    },
    [searchOptions],
  )

  return (
    <Grid flexGrow={1} minHeight={0} gutterSize="small">
      <Grid flexWrap="wrap" gutterSize="small" justifyContent="space-between">
        <Grid.Col>
          <Grid>
            <Grid flexWrap="wrap">
              {deleteOptions.visible && (
                <Grid.Col>
                  <EuiButton color="danger" fill onClick={toggleDeleteModal} isLoading={deleteOptions.isLoading}>
                    {deleteOptions.value}
                  </EuiButton>
                </Grid.Col>
              )}
            </Grid>
          </Grid>
        </Grid.Col>

        <Grid.Col alignSelf={['flex-start', 'flex-end']}>
          <Grid gutterSize={['smallest', 'medium']} flexWrap="wrap">
            <Grid.Col>
              <EuiSearchBar box={{ placeholder: searchOptions.hint, incremental: true }} onChange={onQuery} />
            </Grid.Col>
            {createOptions.visible && (
              <EuiButton
                onClick={createOptions.onClick}
                color="primary"
                fill
                isDisabled={!selectedSite}
                isLoading={isAdding}
              >
                {createOptions.value}
              </EuiButton>
            )}
          </Grid>
        </Grid.Col>
      </Grid>

      <Table
        tableId={TABLE_ID}
        columns={columns}
        items={items}
        selection={selection}
        sorting={sort}
        pagination={pagination}
        paginationOptions={paginationOptions}
        loading={isLoading}
        isCheckable={canManageSizes}
        onItemClicked={canManageSizes ? onItemClicked : undefined}
        selectedItems={selectedSizes}
      />

      {isDeleteModalOpen && (
        <DeleteConfirmationModal
          onClose={toggleDeleteModal}
          onDelete={onDelete}
          itemSize={selectedItems.length || 1}
          intlPrefix={INTL_PREFIX_MODAL}
        />
      )}
    </Grid>
  )
}

export default React.memo(SizesTable)
